import { TwoFAType } from '../../domain/two-factor';
import { TwoFactorError, TwoFactorService, TwoFAResult } from '../../services/two-factor.service';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { I18nService } from '@ppl/i18n';
import { PPL_DIALOG_DATA, PplDialogRef, PplDialogService } from '@ppl/ui/dialog';
import { wrapAPICall } from '@ppl/utils/api.utils';
import { pairs } from '@ppl/utils/array.utils';
import { closeDialogOnNavigationStart, createDialogCloseResponse, createDialogErrorResponse } from '@ppl/utils/dialog.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  throwError
} from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import type {
  OnDestroy,
  OnInit
} from '@angular/core';
import type { DialogResponse } from '@ppl/utils/dialog.utils';
import type {
  Observable,
  Subscription
} from 'rxjs';

@Component({
    selector: 'ppl-two-factor-backup-codes-dialog',
    templateUrl: './two-factor-backup-codes-dialog.component.html',
    styleUrls: ['./two-factor-backup-codes-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorBackupCodesDialogComponent implements OnInit, OnDestroy {

  loading$ = new BehaviorSubject<boolean>(false);
  generating$ = new BehaviorSubject<boolean>(false);

  backupCodes$ = new BehaviorSubject<(string | null)[]>(Array.from({ length: 10 }, () => null));
  backupCodesByPairs$ = this.backupCodes$.pipe(
    map(backupCodes => pairs(backupCodes, 2))
  );
  backupCodesRemainingCount$ = this.backupCodes$.pipe(
    map(backupCodes => {
      return backupCodes.filter(Boolean).length;
    })
  );

  subscriptions: Subscription[] = [];

  constructor(
    @Inject(PPL_DIALOG_DATA) private data: TwoFactorBackupCodesDialogData,
    private dialogRef: PplDialogRef<
      TwoFactorBackupCodesDialogComponent,
      DialogResponse<TwoFactorBackupCodesDialogResponse>
    >,
    private twoFactorService: TwoFactorService,
    private dialogService: PplDialogService,
    private i18nService: I18nService,
    private router: Router
  ) { }

  ngOnInit() {
    this.subscriptions.push(closeDialogOnNavigationStart(this.router, this.dialogRef));
    this.subscriptions.push(
      this.callAPI(this.fetchBackupCodes(), this.loading$).subscribe()
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onCreateNewCodes() {
    this.callAPI(
      this.twoFactorService.setupStart({
        type: TwoFAType.BACKUP_CODES,
        securityToken: this.data.securityToken
      }).pipe(
        switchMap(response => {
          if (response.result !== TwoFAResult.FINISHED) {
            this.dialogService.alert({
              text: this.i18nService.translate('Generating_new_backup_codes_failed.')
            });
            return EMPTY;
          } else {
            return this.fetchBackupCodes();
          }
        })
      ),
      this.generating$
    ).subscribe();
  }

  onCancel() {
    this.dialogRef.close(createDialogCloseResponse());
  }

  private fetchBackupCodes() {
    return this.twoFactorService.getMethodValue({
      id: this.data.twoFactorMethodId,
      securityToken: this.data.securityToken
    }).pipe(
      tap(response => {
        this.backupCodes$.next(response.data);
      })
    );
  }

  private callAPI<T>(observable$: Observable<T>, progress$: BehaviorSubject<boolean>): Observable<T> {
    return defer(() => {
      return wrapAPICall(observable$, progress$).pipe(
        this.twoFactorService.handleError({
          errorHandlerFn: (errorInfo, originalError) => {
            if (errorInfo.error === TwoFactorError.INVALID_OR_EXPIRED_TOKEN) {
              this.dialogService.alert({
                text: this.i18nService.translate('Your_security_session_expired.'),
                actionLabel: this.i18nService.translate('Ok')
              }).subscribe(() => {
                this.dialogRef.close(createDialogErrorResponse({ errorInfo }));
              });
              return EMPTY;
            } else {
              return throwError(originalError);
            }
          }
        })
      );
    });
  }
}

export interface TwoFactorBackupCodesDialogData {
  twoFactorMethodId: number;
  securityToken: string;
}

export interface TwoFactorBackupCodesDialogResponse {
}
